import React from "react";
import { Box, Typography } from "@mui/material";

const sections = [
  {
    heading: "1. Introduction",
    body: `Welcome to Inteliphyle LLP ("Inteliphyle", "we", "our", or "us"). We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website https://inteliphyle.com/ or interact with our services. By accessing or using our website, you consent to the practices described in this Privacy Policy.`,
  },
  {
    heading: "2. Information We Collect",
    subsections: [
      {
        subheading: "Personal Information",
        body: `Information you voluntarily provide when you:`,
        bullets: [
          "Contact us via forms",
          "Subscribe to newsletters",
          "Request consulting services",
          "Send inquiries via email",
        ],
        tail: `This may include:`,
        tailBullets: [
          "Name",
          "Email address",
          "Phone number",
          "Company name",
          "Job title",
          "Any information you provide in your message",
        ],
      },
      {
        subheading: "Automatically Collected Information",
        body: `When you visit our website, certain information may be automatically collected, including:`,
        bullets: [
          "IP address",
          "Browser type",
          "Device information",
          "Pages visited",
          "Time and date of visit",
          "Referral source",
        ],
      },
      {
        subheading: "Cookies & Tracking Technologies",
        body: `We may use cookies or analytics tools to enhance user experience and analyze website traffic. Users can modify their browser settings to decline cookies.`,
      },
    ],
  },
  {
    heading: "3. How We Use Your Information",
    body: `We use the collected information for purposes including:`,
    bullets: [
      "Responding to inquiries or requests",
      "Providing advisory or consulting services",
      "Improving website functionality",
      "Sending updates or relevant communications",
      "Analyzing website usage and performance",
      "Ensuring security and preventing fraud",
    ],
  },
  {
    heading: "4. Legal Basis for Processing",
    body: `We process personal data based on:`,
    bullets: [
      "Your consent",
      "Legitimate business interests",
      "Compliance with legal obligations",
    ],
  },
  {
    heading: "5. Sharing of Information",
    body: `We do not sell your personal information. However, we may share information with:`,
    bullets: [
      "Trusted service providers (hosting, analytics, email services)",
      "Professional advisors or legal authorities if required by law",
      "Partners or contractors assisting with service delivery",
    ],
    tail: `All third parties are required to maintain confidentiality and data protection standards.`,
  },
  {
    heading: "6. Data Security",
    body: `We implement reasonable technical and organizational security measures to protect personal information from:`,
    bullets: [
      "Unauthorized access",
      "Disclosure",
      "Alteration",
      "Loss or misuse",
    ],
    tail: `However, no method of internet transmission is completely secure.`,
  },
  {
    heading: "7. Data Retention",
    body: `We retain personal data only for as long as necessary to:`,
    bullets: [
      "Fulfill the purposes outlined in this policy",
      "Comply with legal or regulatory obligations",
      "Resolve disputes or enforce agreements",
    ],
  },
  {
    heading: "8. Your Rights",
    body: `Depending on applicable laws, you may have the right to:`,
    bullets: [
      "Access your personal data",
      "Request correction or deletion",
      "Withdraw consent",
      "Request information about how your data is processed",
    ],
    tail: `Requests may be submitted using the contact details below.`,
  },
  {
    heading: "9. Third-Party Links",
    body: `Our website may contain links to external websites. We are not responsible for the privacy practices of those third-party websites.`,
  },
  {
    heading: "10. Children's Privacy",
    body: `Our website and services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children.`,
  },
  {
    heading: "11. Updates to This Policy",
    body: `We may update this Privacy Policy from time to time. Updates will be posted on this page with the revised date.`,
  },
  {
    heading: "12. Grievance Officer / Contact Information",
    body: `Inteliphyle LLP\nFlat No. 1303, 13th Floor, T5 Emerald Isle\nSaki Vihar Road, Chandivali, Powai\nMumbai, Maharashtra 400072\nIndia`,
  },
];

const SectionBlock = ({ section }) => (
  <Box sx={{ mb: "2.5rem" }}>
    <Typography
      sx={{
        fontFamily: "Plus Jakarta Sans",
        fontSize: { xs: "1.1rem", md: "1.25rem" },
        fontWeight: 700,
        color: "#1a1a2e",
        mb: "0.75rem",
      }}
    >
      {section.heading}
    </Typography>

    {section.body && (
      <Typography
        sx={{
          fontFamily: "Plus Jakarta Sans",
          fontSize: { xs: "0.9rem", md: "1rem" },
          color: "#444 !important",
          lineHeight: 1.8,
          whiteSpace: "pre-line",
          mb: section.bullets ? "0.5rem" : "0",
        }}
      >
        {section.body}
      </Typography>
    )}

    {section.bullets && (
      <Box component="ul" sx={{ pl: "1.5rem", mt: "0.25rem", mb: section.tail ? "0.75rem" : 0 }}>
        {section.bullets.map((b, i) => (
          <Box
            component="li"
            key={i}
            sx={{
              fontFamily: "Plus Jakarta Sans",
              fontSize: { xs: "0.9rem", md: "1rem" },
              color: "#444 !important",
              lineHeight: 1.8,
            }}
          >
            {b}
          </Box>
        ))}
      </Box>
    )}

    {section.tail && (
      <Typography
        sx={{
          fontFamily: "Plus Jakarta Sans",
          fontSize: { xs: "0.9rem", md: "1rem" },
          color: "#444 !important",
          lineHeight: 1.8,
        }}
      >
        {section.tail}
      </Typography>
    )}

    {section.subsections &&
      section.subsections.map((sub, idx) => (
        <Box key={idx} sx={{ mt: "1.2rem", pl: { xs: "0", md: "1rem" } }}>
          <Typography
            sx={{
              fontFamily: "Plus Jakarta Sans",
              fontSize: { xs: "1rem", md: "1.1rem" },
              fontWeight: 600,
              color: "#1a1a2e",
              mb: "0.5rem",
            }}
          >
            {sub.subheading}
          </Typography>
          {sub.body && (
            <Typography
              sx={{
                fontFamily: "Plus Jakarta Sans",
                fontSize: { xs: "0.9rem", md: "1rem" },
                color: "#444 !important",
                lineHeight: 1.8,
                mb: sub.bullets ? "0.4rem" : 0,
              }}
            >
              {sub.body}
            </Typography>
          )}
          {sub.bullets && (
            <Box component="ul" sx={{ pl: "1.5rem", mt: "0.25rem", mb: sub.tail ? "0.5rem" : 0 }}>
              {sub.bullets.map((b, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    fontFamily: "Plus Jakarta Sans",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    color: "#444 !important",
                    lineHeight: 1.8,
                  }}
                >
                  {b}
                </Box>
              ))}
            </Box>
          )}
          {sub.tail && (
            <Typography
              sx={{
                fontFamily: "Plus Jakarta Sans",
                fontSize: { xs: "0.9rem", md: "1rem" },
                color: "#444 !important",
                lineHeight: 1.8,
              }}
            >
              {sub.tail}
            </Typography>
          )}
          {sub.tailBullets && (
            <Box component="ul" sx={{ pl: "1.5rem", mt: "0.25rem" }}>
              {sub.tailBullets.map((b, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    fontFamily: "Plus Jakarta Sans",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    color: "#444 !important",
                    lineHeight: 1.8,
                  }}
                >
                  {b}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ))}
  </Box>
);

const PrivacyPolicy = () => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f9f9f9",
        pt: { xs: "8rem", md: "7rem" },
        pb: { xs: "4rem", md: "6rem" },
        px: { xs: "1.5rem", sm: "3rem", md: "8rem", lg: "12rem" },
      }}
    >
      {/* Page Title */}
      <Typography
        sx={{
          fontFamily: "Plus Jakarta Sans",
          fontSize: { xs: "1.8rem", md: "2.8rem" },
          fontWeight: 800,
          color: "#1a1a2e",
          mb: "0.5rem",
        }}
      >
        Privacy Policy
      </Typography>


      {/* Sections */}
      {sections.map((section, index) => (
        <SectionBlock key={index} section={section} />
      ))}
    </Box>
  );
};

export default PrivacyPolicy;
